/* Global audio player provider. Manages a hidden <audio> element, playback state,
   position persistence (localStorage cache + Convex), sleep timer, and MediaSession API. */

import { useState, useRef, useCallback, useEffect, useMemo, type ReactNode } from "react";
import { useMutation, useQuery } from "convex/react";
import { api } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import type { PodcastTrack, PlaybackState, AudioPlayerState } from "@/types/audioPlayer";
import { AudioPlayerContext, type AudioPlayerContextValue } from "@/contexts/audioPlayerContextValue";
import { useKiwiReward } from "@/hooks/useKiwiReward";
import type { KiwiSkinConfig } from "@/types/kiwiSkin";
import type { SkipSegment } from "@/types/skipSegment";
import { posthog } from "@/lib/posthog";
import { Events } from "@/lib/posthogEvents";

const POSITION_SAVE_INTERVAL_MS = 10_000;
const POSITION_CACHE_PREFIX = "podcast-pos-";
const DEFAULT_SKIP_BACK = 15;
const DEFAULT_SKIP_FORWARD = 30;

function getCachedPosition(feedItemId: string): number | null {
  const raw = localStorage.getItem(POSITION_CACHE_PREFIX + feedItemId);
  return raw ? Number(raw) : null;
}

function setCachedPosition(feedItemId: string, seconds: number) {
  localStorage.setItem(POSITION_CACHE_PREFIX + feedItemId, String(seconds));
}

interface AudioPlayerProviderProps {
  children: ReactNode;
}

export function AudioPlayerProvider({ children }: AudioPlayerProviderProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [track, setTrack] = useState<PodcastTrack | null>(null);
  const [playbackState, setPlaybackState] = useState<PlaybackState>("idle");
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [bufferedEnd, setBufferedEnd] = useState(0);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [sleepTimerRemaining, setSleepTimerRemaining] = useState<number | null>(null);

  const sleepTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const sleepEndOfEpisodeRef = useRef(false);
  const positionSaveRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const rafRef = useRef<number | null>(null);
  const trackRef = useRef<PodcastTrack | null>(null);

  const upsertPosition = useMutation(api.playbackPositions.upsertPosition);
  const markAsCompleted = useMutation(api.feedItems.markAsCompleted);
  const { triggerReward } = useKiwiReward();

  // Keep trackRef in sync
  useEffect(() => {
    trackRef.current = track;
  }, [track]);

  // Create audio element once
  useEffect(() => {
    const audio = new Audio();
    audio.preload = "metadata";
    audioRef.current = audio;
    return () => {
      audio.pause();
      audio.src = "";
    };
  }, []);

  // RAF loop for smooth time updates (~4fps for React state)
  const startTimeUpdates = useCallback(() => {
    let lastUpdate = 0;
    const update = () => {
      const audio = audioRef.current;
      if (audio) {
        const now = performance.now();
        if (now - lastUpdate > 250) {
          setCurrentTime(audio.currentTime);
          setDuration(audio.duration || 0);
          if (audio.buffered.length > 0) {
            setBufferedEnd(audio.buffered.end(audio.buffered.length - 1));
          }
          lastUpdate = now;
        }
      }
      rafRef.current = requestAnimationFrame(update);
    };
    rafRef.current = requestAnimationFrame(update);
  }, []);

  const stopTimeUpdates = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  // Save position to Convex (debounced via interval)
  const flushPosition = useCallback(() => {
    const audio = audioRef.current;
    const t = trackRef.current;
    if (!audio || !t || audio.currentTime < 1) return;
    setCachedPosition(t.feedItemId, audio.currentTime);
    void upsertPosition({
      feedItemId: t.feedItemId as Id<"feedItems">,
      positionSeconds: audio.currentTime,
      durationSeconds: audio.duration || undefined,
    });
  }, [upsertPosition]);

  const startPositionSaving = useCallback(() => {
    if (positionSaveRef.current) clearInterval(positionSaveRef.current);
    positionSaveRef.current = setInterval(flushPosition, POSITION_SAVE_INTERVAL_MS);
  }, [flushPosition]);

  const stopPositionSaving = useCallback(() => {
    if (positionSaveRef.current) {
      clearInterval(positionSaveRef.current);
      positionSaveRef.current = null;
    }
  }, []);

  // Flush position on page hide (e.g. tab close, navigation) without triggering
  // the browser's "leave page?" confirmation dialog that beforeunload causes.
  useEffect(() => {
    const handler = () => {
      if (document.visibilityState === "hidden") flushPosition();
    };
    document.addEventListener("visibilitychange", handler);
    return () => document.removeEventListener("visibilitychange", handler);
  }, [flushPosition]);

  // Sleep timer logic
  const clearSleepTimer = useCallback(() => {
    if (sleepTimerRef.current) {
      clearInterval(sleepTimerRef.current);
      sleepTimerRef.current = null;
    }
    sleepEndOfEpisodeRef.current = false;
    setSleepTimerRemaining(null);
  }, []);

  const setSleepTimer = useCallback((minutes: number | "end") => {
    clearSleepTimer();
    if (minutes === "end") {
      sleepEndOfEpisodeRef.current = true;
      setSleepTimerRemaining(-1); // -1 signals "end of episode"
      return;
    }
    let remaining = minutes * 60;
    setSleepTimerRemaining(remaining);
    sleepTimerRef.current = setInterval(() => {
      remaining -= 1;
      setSleepTimerRemaining(remaining);
      if (remaining <= 0) {
        audioRef.current?.pause();
        setPlaybackState("paused");
        flushPosition();
        stopTimeUpdates();
        stopPositionSaving();
        clearSleepTimer();
      }
    }, 1000);
  }, [clearSleepTimer, flushPosition, stopTimeUpdates, stopPositionSaving]);

  // ── Auto-skip logic ──
  const settings = useQuery(api.userSettings.getSettings);
  const autoSkipEnabled = (settings as Record<string, unknown> | null)?.autoSkipSegments === true;

  const extract = useQuery(
    api.contentExtracts.getByFeedItem,
    track ? { feedItemId: track.feedItemId as Id<"feedItems"> } : "skip",
  );

  const skipSegments = useMemo<SkipSegment[]>(() => {
    if (!extract?.skipSegments) return [];
    try {
      const parsed = JSON.parse(extract.skipSegments);
      return Array.isArray(parsed) ? parsed : [];
    } catch { return []; }
  }, [extract?.skipSegments]);

  // Track which segments were already auto-skipped (reset on track change)
  const autoSkippedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    autoSkippedRef.current = new Set();
  }, [track?.feedItemId]);

  // Auto-skip effect: when enabled, skip segments as playback enters them
  useEffect(() => {
    if (!autoSkipEnabled || skipSegments.length === 0 || playbackState !== "playing") return;
    const audio = audioRef.current;
    if (!audio) return;

    const key = skipSegments.find((s) => {
      const k = `${s.startTime}-${s.endTime}`;
      return currentTime >= s.startTime && currentTime < s.endTime && !autoSkippedRef.current.has(k);
    });
    if (key) {
      const k = `${key.startTime}-${key.endTime}`;
      autoSkippedRef.current.add(k);
      audio.currentTime = key.endTime;
      setCurrentTime(key.endTime);
    }
  }, [autoSkipEnabled, skipSegments, currentTime, playbackState]);

  // MediaSession setup
  const updateMediaSession = useCallback((t: PodcastTrack) => {
    if (!("mediaSession" in navigator)) return;
    navigator.mediaSession.metadata = new MediaMetadata({
      title: t.title,
      artist: t.sourceName || "Podcast",
      artwork: t.thumbnail ? [{ src: t.thumbnail, sizes: "512x512", type: "image/jpeg" }] : [],
    });
    navigator.mediaSession.setActionHandler("play", () => {
      audioRef.current?.play();
      setPlaybackState("playing");
      startTimeUpdates();
      startPositionSaving();
    });
    navigator.mediaSession.setActionHandler("pause", () => {
      audioRef.current?.pause();
      setPlaybackState("paused");
      flushPosition();
      stopTimeUpdates();
      stopPositionSaving();
    });
    navigator.mediaSession.setActionHandler("seekbackward", () => {
      const audio = audioRef.current;
      if (audio) audio.currentTime = Math.max(0, audio.currentTime - DEFAULT_SKIP_BACK);
    });
    navigator.mediaSession.setActionHandler("seekforward", () => {
      const audio = audioRef.current;
      if (audio) audio.currentTime = Math.min(audio.duration || 0, audio.currentTime + DEFAULT_SKIP_FORWARD);
    });
    navigator.mediaSession.setActionHandler("seekto", (details) => {
      const audio = audioRef.current;
      if (audio && details.seekTime != null) audio.currentTime = details.seekTime;
    });
  }, [flushPosition, startTimeUpdates, stopTimeUpdates, startPositionSaving, stopPositionSaving]);

  const updateMediaSessionState = useCallback((state: PlaybackState) => {
    if ("mediaSession" in navigator) {
      navigator.mediaSession.playbackState = state === "playing" ? "playing" : state === "paused" ? "paused" : "none";
    }
  }, []);

  // Core play function
  const play = useCallback((newTrack: PodcastTrack) => {
    const audio = audioRef.current;
    if (!audio) return;

    // If same track, just resume
    if (trackRef.current?.feedItemId === newTrack.feedItemId && audio.src) {
      audio.play();
      setPlaybackState("playing");
      updateMediaSessionState("playing");
      startTimeUpdates();
      startPositionSaving();
      return;
    }

    setTrack(newTrack);
    setPlaybackState("loading");
    posthog.capture(Events.PODCAST_PLAY_STARTED, { feedItemId: newTrack.feedItemId, title: newTrack.title });
    setCurrentTime(0);
    setDuration(0);
    setBufferedEnd(0);

    audio.src = newTrack.audioUrl;
    audio.playbackRate = playbackSpeed;

    const handleCanPlay = () => {
      // Resume from cached position
      const cached = getCachedPosition(newTrack.feedItemId);
      if (cached && cached > 1) {
        audio.currentTime = cached;
      }
      audio.play();
      setPlaybackState("playing");
      updateMediaSessionState("playing");
      startTimeUpdates();
      startPositionSaving();
      audio.removeEventListener("canplay", handleCanPlay);
    };
    audio.addEventListener("canplay", handleCanPlay);

    updateMediaSession(newTrack);
    audio.load();
  }, [playbackSpeed, startTimeUpdates, startPositionSaving, updateMediaSession, updateMediaSessionState]);

  // Audio ended handler
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleEnded = async () => {
      const t = trackRef.current;
      stopTimeUpdates();
      stopPositionSaving();
      setPlaybackState("idle");
      updateMediaSessionState("idle");

      if (t) {
        // Clear cached position
        localStorage.removeItem(POSITION_CACHE_PREFIX + t.feedItemId);
        posthog.capture(Events.PODCAST_COMPLETED, { feedItemId: t.feedItemId, title: t.title });
        // Auto-complete the feed item
        try {
          const result = await markAsCompleted({ id: t.feedItemId as Id<"feedItems"> });
          const awarded = (result as { awardedSkin?: KiwiSkinConfig } | undefined)?.awardedSkin;
          if (awarded) triggerReward(awarded);
        } catch {
          // non-critical
        }
      }

      if (sleepEndOfEpisodeRef.current) {
        clearSleepTimer();
      }

      setTrack(null);
      setCurrentTime(0);
      setDuration(0);
    };

    audio.addEventListener("ended", handleEnded);
    return () => audio.removeEventListener("ended", handleEnded);
  }, [stopTimeUpdates, stopPositionSaving, markAsCompleted, triggerReward, clearSleepTimer, updateMediaSessionState]);

  const pause = useCallback(() => {
    audioRef.current?.pause();
    setPlaybackState("paused");
    updateMediaSessionState("paused");
    flushPosition();
    stopTimeUpdates();
    stopPositionSaving();
  }, [flushPosition, stopTimeUpdates, stopPositionSaving, updateMediaSessionState]);

  const resume = useCallback(() => {
    audioRef.current?.play();
    setPlaybackState("playing");
    updateMediaSessionState("playing");
    startTimeUpdates();
    startPositionSaving();
  }, [startTimeUpdates, startPositionSaving, updateMediaSessionState]);

  const togglePlayPause = useCallback(() => {
    if (playbackState === "playing") pause();
    else if (playbackState === "paused") resume();
  }, [playbackState, pause, resume]);

  const seek = useCallback((seconds: number) => {
    const audio = audioRef.current;
    if (audio) {
      audio.currentTime = Math.max(0, Math.min(seconds, audio.duration || 0));
      setCurrentTime(audio.currentTime);
    }
  }, []);

  const skipForward = useCallback((seconds = DEFAULT_SKIP_FORWARD) => {
    const audio = audioRef.current;
    if (audio) {
      audio.currentTime = Math.min(audio.duration || 0, audio.currentTime + seconds);
      setCurrentTime(audio.currentTime);
    }
  }, []);

  const skipBack = useCallback((seconds = DEFAULT_SKIP_BACK) => {
    const audio = audioRef.current;
    if (audio) {
      audio.currentTime = Math.max(0, audio.currentTime - seconds);
      setCurrentTime(audio.currentTime);
    }
  }, []);

  const setSpeed = useCallback((speed: number) => {
    setPlaybackSpeed(speed);
    if (audioRef.current) audioRef.current.playbackRate = speed;
  }, []);

  const stop = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.src = "";
    }
    flushPosition();
    stopTimeUpdates();
    stopPositionSaving();
    clearSleepTimer();
    setTrack(null);
    setPlaybackState("idle");
    setCurrentTime(0);
    setDuration(0);
    setBufferedEnd(0);
    updateMediaSessionState("idle");
  }, [flushPosition, stopTimeUpdates, stopPositionSaving, clearSleepTimer, updateMediaSessionState]);

  const state: AudioPlayerState = {
    track,
    playbackState,
    currentTime,
    duration,
    bufferedEnd,
    playbackSpeed,
    sleepTimerRemaining,
  };

  const value: AudioPlayerContextValue = {
    state,
    play,
    pause,
    resume,
    togglePlayPause,
    seek,
    skipForward,
    skipBack,
    setSpeed,
    setSleepTimer,
    cancelSleepTimer: clearSleepTimer,
    stop,
  };

  return (
    <AudioPlayerContext value={value}>
      {children}
    </AudioPlayerContext>
  );
}
